import { useMemo, useState } from "react";
import { IconContentCopy, RadioGroup } from "@cdkglobal/radial";
import { useUIStateContext } from "../../../../../contexts/UIStateContext";
import {
  RowData,
  RowDataReducerAction,
  RowDataReducerState,
} from "../../../../../types";
import { getTabbedSectionKeys } from "../../../helpers/schemaHelpers";
import {
  CopyRadioGroup,
  DiscountCopySettingsCopyButton,
  DiscountCopySettingsRow,
} from "../../../../../utils/styled";
import {
  PAY_SETTINGS_TABS,
  PaySettingsTab,
  TAB_LABELS,
  TAB_SUFFIX,
  isPaySettingsTab,
  getNestedFieldKeys,
  replaceSuffix,
  transformValueSuffixes,
  getTargetTabsLabel,
} from "./discountsPayloadHelpers";

const ALL_OTHERS_OPTION = "all-others";
const COPY_SETTINGS_FIELD_KEY = "copy_discount_settings";

export default function CopyDiscountSettings({
  rowDataReducerState,
  dispatch,
}: Readonly<{
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}>): JSX.Element | null {
  const [selectedCopyTarget, setSelectedCopyTarget] = useState("");
  const { activeTabIndex, setRecordPageToast } = useUIStateContext();
  const { rowData, schema, isReadOnly } = rowDataReducerState;

  const tabKeys = getTabbedSectionKeys(schema.sections);
  const sourceTab = tabKeys[activeTabIndex] ?? "";
  const targetTabs = PAY_SETTINGS_TABS.filter((tab) => tab !== sourceTab);

  const targetFieldSets = useMemo(
    () =>
      Object.fromEntries(
        targetTabs.map((tab) => [
          tab,
          new Set(getNestedFieldKeys(schema.sections[tab])),
        ]),
      ) as Record<string, Set<string>>,
    [schema.sections, sourceTab],
  );

  if (!isPaySettingsTab(sourceTab)) return null;

  const sourceSuffix = TAB_SUFFIX[sourceTab];
  const sourceSectionFields = getNestedFieldKeys(
    schema.sections[sourceTab],
  ).filter((field) => field !== COPY_SETTINGS_FIELD_KEY);

  const options = [
    ...targetTabs.map((tab) => ({
      id: `copy-discount-settings-${sourceTab}-${tab}`,
      label: TAB_LABELS[tab],
      value: tab,
      ariaLabel: TAB_LABELS[tab],
    })),
    {
      id: `copy-discount-settings-${sourceTab}-all`,
      label: "Both",
      value: ALL_OTHERS_OPTION,
      ariaLabel: "Both",
    },
  ];

  const handleCopy = () => {
    const copyTargets =
      selectedCopyTarget === ALL_OTHERS_OPTION
        ? [...targetTabs]
        : [selectedCopyTarget as PaySettingsTab];

    const updatedRowData: RowData = { ...rowData };

    copyTargets.forEach((targetTab) => {
      const targetSuffix = TAB_SUFFIX[targetTab];
      const targetFieldSet = targetFieldSets[targetTab] ?? new Set<string>();

      sourceSectionFields.forEach((sourceFieldKey) => {
        const targetFieldKey = replaceSuffix(
          sourceFieldKey,
          sourceSuffix,
          targetSuffix,
        );
        if (!targetFieldSet.has(targetFieldKey)) return;
        updatedRowData[targetFieldKey] = transformValueSuffixes(
          rowData[sourceFieldKey],
          sourceSuffix,
          targetSuffix,
        ) as RowData[keyof RowData];
      });
    });

    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: { rowData: updatedRowData },
    });
    setRecordPageToast({
      variant: "positive",
      message: `Fields copied successfully to ${getTargetTabsLabel(
        copyTargets,
      )}.`,
    });
  };

  return (
    <DiscountCopySettingsRow data-testid="copy-discount-settings-panel">
      <CopyRadioGroup>
        <RadioGroup
          id={`copy-discount-settings-${sourceTab}`}
          dataTestId={`copy-discount-settings-${sourceTab}`}
          name={`copy-discount-settings-radio-group-${sourceTab}`}
          hideLabel
          label={TAB_LABELS[sourceTab]}
          options={options}
          value={selectedCopyTarget}
          isDisabled={isReadOnly}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (isReadOnly) return;
            setSelectedCopyTarget(event.target.value);
          }}
        />
      </CopyRadioGroup>

      <DiscountCopySettingsCopyButton
        dataTestId="copy-discount-settings-button"
        icon={<IconContentCopy />}
        iconColor="primary"
        onClick={handleCopy}
        text="Copy"
        isDisabled={
          !!isReadOnly ||
          !selectedCopyTarget ||
          sourceSectionFields.length === 0
        }
      />
    </DiscountCopySettingsRow>
  );
}
